import 'vite/modulepreload-polyfill'
// Critical imports that need to be loaded immediately
import * as AlpineModule from './modules/alpine';
import * as RendererModule from './modules/renderer';
import * as GlobalsModule from './modules/globals';
import * as ShopifyModule from './modules/shopify';
import enhancedEventSystem from './modules/enhancedEventSystem';
import ProductContextManager from '@src/contexts/ProductContextManager';
import currencyManager from '../src/utils/currencyManager';

// Performance monitoring utility
const perfMonitor = {
  marks: new Map(),
  mark(name) {
    if (window.performance?.mark) {
      performance.mark(name);
      this.marks.set(name, performance.now());
    }
  },
  measure(name, startMark) {
    if (window.performance?.measure && this.marks.has(startMark)) {
      try {
        performance.measure(name, startMark);
        const duration = performance.now() - this.marks.get(startMark);
        if (duration > 16) { // Log anything slower than one frame
          console.warn(`⚠️ Slow operation: ${name} took ${duration.toFixed(2)}ms`);
        }
        return duration;
      } catch (e) {
        // Ignore measurement errors
      }
    }
    return 0;
  }
};

let getAlpineStore, mobileBreakpoint;

async function initializeTheme() {
    perfMonitor.mark('theme-init-start');

    try {
        // Initialize the enhanced dynamic event system first
        perfMonitor.mark('event-system-start');
        await enhancedEventSystem.init(false);
        perfMonitor.measure('event-system-init', 'event-system-start');

        // Initialize critical Alpine.js functionality
        perfMonitor.mark('alpine-start');
        AlpineModule.start();
        getAlpineStore = AlpineModule.getAlpineStore;
        mobileBreakpoint = GlobalsModule.getMobileBreakpoint();
        perfMonitor.measure('alpine-init', 'alpine-start');

        // Load modules with priority-based scheduling
        const loadModules = () => {
            perfMonitor.mark('modules-load-start');

            // Use scheduler API for better timing
            if (window.scheduler?.postTask) {
                scheduler.postTask(() => {
                    ShopifyModule.addListeners();
                    RendererModule.addListeners();
                }, { priority: 'user-blocking' });
            } else {
                ShopifyModule.addListeners();
                RendererModule.addListeners();
            }

            perfMonitor.measure('modules-loaded', 'modules-load-start');
        };

        // Use the fastest available scheduling mechanism
        if (window.scheduler?.postTask) {
            scheduler.postTask(loadModules, { priority: 'user-blocking' });
        } else if ('requestIdleCallback' in window) {
            requestIdleCallback(loadModules, { timeout: 100 });
        } else {
            // Immediate execution for older browsers
            setTimeout(loadModules, 0);
        }

        // Optimize heavy dependency loading with content detection
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                // Only preload if likely to be needed
                const hasAnimations = document.querySelector('[data-theme-id*="animation"], [class*="animate-"], [data-aos]');
                if (hasAnimations && typeof window.FramerMotion === 'undefined') {
                    import('framer-motion').then(() => {
                        perfMonitor.mark('framer-motion-loaded');
                    }).catch(() => {});
                }

                // Only preload search if search elements exist
                const hasSearch = document.querySelector('[data-search], [data-theme-id*="search"], [data-theme-id*="filter"]');
                if (hasSearch) {
                    import('algoliasearch').then(() => {
                        perfMonitor.mark('algolia-loaded');
                    }).catch(() => {});
                }
            }, { timeout: 1000 });
        }

        perfMonitor.measure('theme-init-complete', 'theme-init-start');

    } catch (e) {
        console.error('Arctheme failed to start.', e.message);
    }
}

// Critical path optimization - start immediately
const startTheme = () => {
    perfMonitor.mark('dom-ready');
    initializeTheme();
};

// Initialize theme as early as possible
if (document.readyState === 'complete') {
    // Already loaded, render immediately
    startTheme();
} else if (document.readyState === 'interactive') {
    // DOM ready but resources loading, start immediately
    startTheme();
} else {
    // Still loading, wait for DOM
    document.addEventListener('DOMContentLoaded', startTheme, { once: true, passive: true });
}

// Add performance observer for debugging
if (window.PerformanceObserver && typeof PerformanceObserver === 'function') {
    try {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                if (entry.duration > 50) { // Log anything over 50ms
                    console.log(`🐌 Performance: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
                }
            }
        });
        observer.observe({ entryTypes: ['measure'] });
    } catch (e) {
        // Ignore if PerformanceObserver not supported
    }
}

// Export for potential external access
export { getAlpineStore, mobileBreakpoint, perfMonitor };
